import { SyntheticEvent } from 'react';
import { range } from 'd3-array';
import NO_IAMGE from '@assets/images/empty.png';
import { getYear } from 'date-fns';

import theme from '@styles/theme';
import dayjs from 'dayjs';
import { sidoParams } from '@lib/data';

export const onImageError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = NO_IAMGE;
};

// 이메일 정규식
export const isEmail = (email: string) => {
    const emailRegex =
        /^(([^<>()\[\].,;:\s@"]+(\.[^<>()\[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
    return emailRegex.test(email);
};
// url 정규식
export const isUrl = (url: string) => {
    const urlRegex = /^(http|https):\/\/[\w\-]+(\.[\w\-]+)+[/#?]?.*$/;
    return urlRegex.test(url);
};
// 휴대폰 번호 Hyphen 삽입
export const autoHyphenCellphone = (value: string) => {
    return value
        .replace(/[^0-9]/g, '')
        .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, '$1-$2-$3')
        .replace(/(\-{1,2})$/g, '');
};
// 전화번호 Hyphen 삽입
export const autoHyphen = (value: string) => {
    return value
        .replace(/[^0-9]/g, '')
        .replace(
            /(^02|^050[0-9]|^1[0-9]{3}|^0[0-9]{2})([0-9]+)?([0-9]{4})$/,
            '$1-$2-$3'
        )
        .replace('--', '-');
};
// 사업자등록번호 Hyphen 삽입
export const autoHyphenLicense = (str: string) => {
    str = str.replace(/[^0-9]/g, '');
    let tmp = '';
    if (str.length < 4) {
        return str;
    } else if (str.length < 6) {
        tmp += str.substring(0, 3);
        tmp += '-';
        tmp += str.substring(3);
        return tmp;
    } else if (str.length < 11) {
        tmp += str.substring(0, 3);
        tmp += '-';
        tmp += str.substring(3, 5);
        tmp += '-';
        tmp += str.substring(5);
        return tmp;
    }

    return str;
};
// 숫자만 입력 가능
export const onlyNumber = (text: string) => {
    return text.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
};
// 영문 대소문자 포함 여부
export const isRequiredChars = (password: string) => {
    const reg = /(?=.*?[a-z])(?=.*?[A-Z])/;
    return reg.test(password);
};
// 숫자 특수문자 포함 여부
export const isNumericSpecialChars = (password: string) => {
    const reg = /(?=.*?[0-9])(?=.*?[#?!@$%^&*-])/;
    return reg.test(password);
};
// 문자열 길이 8~16자리 체크 여부
export const isRequiredLength = (password: string) => {
    const reg = /^.{8,16}$/;
    return reg.test(password);
};
// 반복되는 문자 체크 여부 - 반복되면 true
export const isRepeatedChars = (password: string) => {
    const reg = /(.)\1+/;
    return reg.test(password);
};
/**
 * todo 년도 구하기
 * @param {string} format todo 날짜 포맷
 * @param {number} start todo 시작년도
 * @param {number} end todo 종료년도
 */
export const getYearOption = (
    format?: string,
    start?: number,
    end?: number
) => {
    const years: string[] | number[] = range(
        start || getYear(new Date()),
        end || getYear(new Date()) + 10,
        1
    ).reverse();

    switch (format) {
        case 'YYYY년':
            return years.map((year) => `${year}년`);
        default:
            return years;
    }
};
// 월 옵션
export const getMonthOption = () => {
    const months: string[] | number[] = range(1, 13);

    return months.map((month) => `${month}월`);
};
// 한글 요일
export const dayToString = (day: number) => {
    if (day === 0) return '일';
    else if (day === 1) return '월';
    else if (day === 2) return '화';
    else if (day === 3) return '수';
    else if (day === 4) return '목';
    else if (day === 5) return '금';
    else if (day === 6) return '토';
};
// 일자 옵션
export const getDaysOptions = () => {
    const options = [...Array(31)].map((_, index) => `매달 ${index + 1}일`);
    return [...options, '매달 말일'];
};
// 연속일수 옵션
export const getRepeatedDaysOptions = () => {
    const options = [...Array(31)].map((_, index) => {
        return { title: `${index}일 이상`, value: index };
    });
    return options;
};

// 모달 오픈 시 body 스크롤 제어
export const handleBodyScroll = (property: 'hidden' | 'initial') => {
    document.body.style.overflow = property;
};
// 경과 시간
export const elapsedTime = (date: string) => {
    const start = new Date(date);
    const end = new Date();

    const seconds = Math.floor((end.getTime() - start.getTime()) / 1000);
    if (seconds < 60) return '방금 전';

    const minutes = seconds / 60;
    if (minutes < 60) return `${Math.floor(minutes)}분 전`;

    const hours = minutes / 60;
    if (hours < 24) return `${Math.floor(hours)}시간 전`;

    const days = hours / 24;
    if (days < 7) return `${Math.floor(days)}일 전`;

    return `${dayjs(start).format('M월 DD일')}`;
};
// ms => 00h 00m 포맷
export const msToTime = (ms: number) => {
    const totalMinutes = Math.floor(ms / 1000 / 60);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
};
// input 오토 포커스
export const autofocusHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    ref: React.RefObject<HTMLInputElement>
) => {
    const { value, maxLength } = e.target;
    if (value.length === maxLength) {
        ref.current!.focus();
    }
};
// 뱃지 색상 함수
export const getBadgeColor = (status: string) => {
    switch (status) {
        case 'gray':
            return [
                `${theme.colors.deepGrayFontColor}`,
                `${theme.colors.ultraLightGrayBorder}`
            ];
        case 'darkGray':
            return [
                `${theme.colors.deepGrayFontColor}`,
                `${theme.colors.sidebarGrayFontColor}`
            ];
        case 'green':
            return [
                `${theme.colors.badgeTextGreen}`,
                `${theme.colors.badgeBgGreen}`
            ];
        case 'blue':
            return [
                `${theme.colors.positiveBlue}`,
                `${theme.colors.badgeBgBlue}`
            ];
        case 'yellow':
            return [
                `${theme.colors.badgeTextYellow}`,
                `${theme.colors.badgeBgYellow}`
            ];
        case 'brightYellow':
            return [`#FFBC4F`, `#FCF3C0`];
        case 'red':
            return [`${theme.colors.activeOrange}`, `#F1E1D9`];
        case 'orange':
            return [
                `${theme.colors.activeOrange}`,
                `${theme.colors.badgeBgOrange}`
            ];
        case 'purple':
            return [
                `${theme.colors.badgeTextPurple}`,
                `${theme.colors.badgeBgPurple}`
            ];
        default:
            return [
                `${theme.colors.deepGrayFontColor}`,
                `${theme.colors.ultraLightGrayBorder}`
            ];
    }
};
// 2D 데이터 API 호출
export const fetchRegionData = async (params: any) => {
    const urlParams = new URLSearchParams(params).toString();
    try {
        const res = await fetch(`/req/data?${urlParams}`);
        const json = await res.json();
        console.log('fetch success');
        return json;
    } catch (error) {
        console.log('fetch error: ', error);
    }
};
// 시도 옵션 데이터
export const getSido = async () => {
    const data = await fetchRegionData(sidoParams);
    const dataArr = data.response.result.featureCollection.features.map(
        (info: any) => info.properties.ctp_kor_nm
    ) as string[];
    return dataArr;
};
// // 시/군/구 옵션 데이터
// export const getSigoonInSido = async (selectedSido: string) => {
//     const data = await fetchRegionData({
//         ...sidoParams,
//         data: 'LT_C_ADSIGG_INFO',
//         attrfilter: `full_nm:like:${selectedSido}`
//     });
//     const dataArr = data.response.result.featureCollection.features.map(
//         (info: any) => info.properties.sig_kor_nm
//     ) as string[];
//     return dataArr;
// };
// 읍면동 옵션 데이터
export const getDongInSigoon = async (selectedSigoon: string) => {
    const data = await fetchRegionData({
        ...sidoParams,
        data: 'LT_C_ADEMD_INFO',
        attrfilter: `full_nm:like:${selectedSigoon}`
    });
    const dataArr = data.response.result.featureCollection.features.map(
        (info: any) => info.properties.emd_kor_nm
    ) as string[];
    return dataArr;
};
